import React, { useEffect, useState } from 'react';
import { View, Pressable } from 'react-native';
import { useQuery, useLazyQuery } from '@apollo/client';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import { faPenToSquare, faBan } from '@fortawesome/free-solid-svg-icons';

import { SUGGEST_THINGS } from 'app/src/components/Things/queries';
import { Button } from 'app/src/elements/buttons';
import { Container, ButtonContainer } from 'app/src/elements/ButtonPicker';
import { StyledTextInput, FieldLabel } from 'app/src/elements/inputs';
import Modal from 'app/src/elements/Modal';
import { Text, Subtitle } from 'app/src/styles';

export const EventThingActions = props => {
  const { thing, eventThing, setValues, noNotes } = props;

  const [showEdit, setShowEdit] = useState(false);

  return (
    <>
      {!noNotes && (
        <Pressable onPress={() => setShowEdit(true)}>
          <FontAwesomeIcon
            icon={faPenToSquare}
            color={eventThing.notes ? 'green' : null}
          />
        </Pressable>
      )}

      <Pressable onPress={() => setValues(thing, { selected: false })}>
        <FontAwesomeIcon icon={faBan} />
      </Pressable>

      {showEdit && (
        <Modal title={`${thing.name}`} setShow={setShowEdit}>
          <FieldLabel name="notes" label="Notes" />
          <StyledTextInput
            value={eventThing.notes}
            onChangeText={value => setValues(thing, { notes: value })}
          />
          <Button label="Close" onPress={() => setShowEdit(false)} wide />
        </Modal>
      )}
    </>
  );
};

export const EventThingButton = props => {
  const { thing, eventThing, setValues } = props;

  return (
    <ButtonContainer
      onPress={() => (
        eventThing.selected ? null : setValues(thing, { selected: true })
      )}
      notSelected={!eventThing.selected}
    >
      <Text style={{ fontSize: 40 }}>
        {thing.emoji || thing.name[0]}
      </Text>
      <Text style={{ fontSize: 12 }}>
        {thing.name}
      </Text>

      {eventThing.selected && (
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-around',
            width: '100%'
          }}
        >
          <EventThingActions {...props} />
        </View>
      )}
    </ButtonContainer>
  );
};

export const EventThingListItem = props => {
  const { thing, eventThing } = props;

  return (
    <View style={{ flexDirection: 'row', gap: 20 }}>
      <Text>{thing.name}</Text>

      {!!eventThing.notes && (
        <Text style={{ fontStyle: 'italic' }}>{eventThing.notes}</Text>
      )}

      <EventThingActions {...props} />
    </View>
  );
};

const EventThings = ({ eventThings, setEventThings, watch, ...props }) => {
  const clientId = watch('clientId') || null;
  const eventTypeId = watch('eventTypeId') || null;

  const [showButtons, setShowButtons] = useState(props.showButtons);
  const [showAllButtons, setShowAllButtons] = useState(
    props.showAllButtons || false
  );

  const [things, setThings] = useState();

  const { data } = useQuery(SUGGEST_THINGS, {
    variables: { clientId, eventTypeId, all: showAllButtons }
  });

  useEffect(() => {
    if (data) { setThings(data.things); }
  }, [data]);

  if (!things) { return null; }

  const eventThing = thing => (eventThings[thing.id] || { thingId: thing.id });

  const setValues = (thing, values) => {
    const eventThingData = { ...eventThing(thing), ...values };
    setEventThings({ ...eventThings, [thing.id]: eventThingData });
  };

  const selectedEventThings = Object.keys(eventThings)
    .map(thingId => eventThings[thingId])
    .filter(eventThing => eventThing?.selected);

  return (
    <View>
      <Subtitle>Things</Subtitle>

      {(!showButtons || things.length > 0) && (
        <Pressable onPress={() => setShowButtons(!showButtons)}>
          <Text>[{showButtons ? 'Hide' : 'Show'} Selection Buttons]</Text>
        </Pressable>
      )}

      {showButtons && !showAllButtons && (
        <Pressable onPress={() => setShowAllButtons(true)}>
          <Text>[Show All Buttons]</Text>
        </Pressable>
      )}

      {showButtons && things && (
        <Container>
          {things.map(thing => (
            <EventThingButton
              key={thing.id}
              thing={thing}
              eventThing={eventThing(thing)}
              setValues={setValues}
              noNotes={props.noNotes}
            />
          ))}
        </Container>
      )}

      {selectedEventThings.map(eventThing => (
        <EventThingListItem
          key={eventThing.thingId}
          thing={things.find(thing => thing.id === eventThing.thingId)}
          eventThing={eventThing}
          setValues={setValues}
          noNotes={props.noNotes}
        />
      ))}
    </View>
  );
};

export default EventThings;
